import React, { useState } from "react";
import Tour from "reactour";
import { useHistory } from "react-router-dom";

/**
 * TutorialStrategyTour
 *
 * In-app guided tour for "How to Build a Strategy" (AAPL Pyramid Hedging).
 * Mounts on the real Strategy Builder page when ?tour=build_strategy is in the URL.
 * Points reactour at actual form elements — no fake components.
 */
export default function TutorialStrategyTour({ isOpen }) {
  const history = useHistory();
  const [step, setStep] = useState(0);

  const goToSuggestions = () => {
    history.push("/automation/suggestions");
  };

  const steps = [
    // ── Step 1: Welcome ────────────────────────────────────────────────────────
    {
      selector: '[data-tour="fetch-card"]',
      content: (
        <Step n={1} total={9} title="Welcome — Building a Strategy">
          This tutorial walks you through building a{" "}
          <b>Pyramid Hedging strategy on AAPL</b>. You'll configure the
          strategy, compute the optimal call and accumulation ladder, then
          activate it so the engine can suggest your first covered call sale.
          <br /><br />
          Follow along — you can interact with every field as we go.
        </Step>
      ),
    },

    // ── Step 2: Strategy Type ──────────────────────────────────────────────────
    {
      selector: '[data-tour="strategy-type"]',
      content: (
        <Step n={2} total={9} title="Strategy Type — Pyramid Hedging">
          Select <b>Pyramid Hedging</b> from the dropdown (it should already be
          selected by default).
          <br /><br />
          Pyramid Hedging sells a covered call against your stock position to
          generate weekly or monthly income. Ladder steps below the current
          price let you average down if the stock dips.
        </Step>
      ),
    },

    // ── Step 3: Add AAPL ticker ────────────────────────────────────────────────
    {
      selector: '[data-tour="manual-ticker"]',
      content: (
        <Step n={3} total={9} title='Add AAPL as Your Ticker'>
          Type <b>AAPL</b> into this field, then click <b>+ Add</b>. You can
          also use <em>Fetch Stocks</em> to pull in stocks you already hold —
          but for a brand-new strategy, type the ticker directly here.
          <br /><br />
          The system will load AAPL's current price, available option chain,
          and lot size (100 shares per contract).
        </Step>
      ),
    },

    // ── Step 4: Add button ─────────────────────────────────────────────────────
    {
      selector: '[data-tour="add-ticker"]',
      content: (
        <Step n={4} total={9} title="Click + Add">
          Click <b>+ Add</b> to bring AAPL into the stock list below.
          <br /><br />
          After adding, the table will show AAPL with the current quantity held
          (0 if you don't own shares yet), your delta exposure, and the ladder
          step gap.
        </Step>
      ),
    },

    // ── Step 5: Stock table ────────────────────────────────────────────────────
    {
      selector: '[data-tour="stock-table"]',
      content: (
        <Step n={5} total={9} title="Stock List — Select AAPL">
          AAPL now appears in the list. <b>Check the checkbox</b> next to AAPL
          to select it for building.
          <br /><br />
          The <em>Details</em> button on the right shows a full preview of the
          strategy structure — strike, premium, ladder, and P&L projections. You
          can view it before or after building.
        </Step>
      ),
    },

    // ── Step 6: Advanced Settings ──────────────────────────────────────────────
    {
      selector: '[data-tour="advanced-settings"]',
      content: (
        <Step n={6} total={9} title="Advanced Settings — Key Parameters">
          Click <b>Advanced Settings</b> to expand the configuration panel.
          Key settings for AAPL:
          <ul style={{ paddingLeft: 16, margin: "8px 0 0", lineHeight: 1.8 }}>
            <li><b>Max Investment</b> — total capital for ladder accumulation (e.g. $5,000)</li>
            <li><b>Gap Step %</b> — spacing between ladder buy prices (2% = each step is 2% lower)</li>
            <li><b>Opening Lots</b> — how many option lots to sell initially (1 = 100 shares)</li>
            <li><b>Max Call Lots</b> — ceiling on how many lots the engine will ever hold</li>
            <li><b>Min/Max Call Delta</b> — filters the option chain to OTM calls in your preferred delta range (0.10–0.30 = conservative)</li>
          </ul>
          The defaults work well for a first strategy — you can tune later.
        </Step>
      ),
    },

    // ── Step 7: Build Strategy button ─────────────────────────────────────────
    {
      selector: '[data-tour="build-strategy"]',
      content: (
        <Step n={7} total={9} title="Build Strategy — Compute Everything">
          Click <b>Build Strategy</b>. The engine will:
          <ol style={{ paddingLeft: 16, margin: "8px 0 0", lineHeight: 1.8 }}>
            <li>Scan the AAPL option chain for calls matching your delta range</li>
            <li>Pick the optimal strike and number of lots</li>
            <li>Compute your accumulation ladder (buy prices and quantities for each step)</li>
            <li>Calculate P&L scenarios: if AAPL stays above the strike, dips to each step, or hits the tail-risk level</li>
          </ol>
          Once complete, the <b>Details</b> button shows the full breakdown, and
          the <b>Save Strategy</b> button becomes clickable.
        </Step>
      ),
    },

    // ── Step 8: Save Strategy ──────────────────────────────────────────────────
    {
      selector: '[data-tour="save-strategy"]',
      content: (
        <Step n={8} total={9} title="Save Strategy">
          Once the build is complete, click <b>Save Strategy</b>. This stores
          the strategy configuration — including the computed call, ladder
          steps, and settings — to the database.
          <br /><br />
          A new card will appear below titled <em>Saved — Activate Strategies</em>.
          Click <b>Activate</b> there to make the strategy live.
        </Step>
      ),
    },

    // ── Step 9: What happens next ─────────────────────────────────────────────
    {
      selector: '[data-tour="activate-card"]',
      content: (
        <Step n={9} total={9} title="Activate — Then Go to Trade Suggestions" isLast>
          After saving, click <b>Activate</b> on the strategy row here. This
          marks the strategy ACTIVE and queues the first trade suggestion: a
          covered call sale (SELL_INITIAL_CALL).
          <br /><br />
          Head to <b>Trade Suggestions</b> to review and approve it. The
          suggestion card shows the exact call symbol, strike, premium, and
          the full ladder so you can make an informed decision.
          <br /><br />
          <button
            onClick={goToSuggestions}
            style={goBtn}
          >
            Go to Trade Suggestions →
          </button>
        </Step>
      ),
    },
  ];

  return (
    <Tour
      steps={steps}
      isOpen={isOpen}
      onRequestClose={() => {
        // Remove tour param from URL without full reload
        const url = new URL(window.location.href);
        url.searchParams.delete("tour");
        window.history.replaceState({}, "", url.toString());
        // Force re-render to unmount tour (parent reads isTourMode from URL)
        window.dispatchEvent(new Event("popstate"));
      }}
      goToStep={step}
      getCurrentStep={curr => setStep(curr)}
      rounded={10}
      accentColor="#6f42c1"
      maskSpace={8}
      showNavigationNumber={false}
      showButtons
      showNavigation
      disableInteraction={false}
      lastStepNextButton={<span style={doneStyle}>Finish Tour ✓</span>}
      closeWithMask={false}
    />
  );
}

function Step({ n, total, title, children, isLast }) {
  return (
    <div style={wrap}>
      <div style={counter}>{n} / {total}</div>
      <div style={titleStyle}>{title}</div>
      <div style={body}>{children}</div>
      {isLast && <div style={complete}>Tour complete — happy trading!</div>}
    </div>
  );
}

const wrap      = { maxWidth: 320, fontFamily: "system-ui, sans-serif" };
const counter   = { fontSize: 10, fontWeight: 700, color: "#6f42c1", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 };
const titleStyle = { fontSize: 14, fontWeight: 700, color: "#2c3e50", marginBottom: 8 };
const body      = { fontSize: 12, color: "#444", lineHeight: 1.65 };
const complete  = { marginTop: 10, fontSize: 12, fontWeight: 600, color: "#28a745" };
const doneStyle = { fontSize: 12, fontWeight: 700 };
const goBtn     = {
  marginTop: 10,
  background: "#6f42c1",
  color: "#fff",
  border: "none",
  borderRadius: 6,
  padding: "7px 14px",
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 700,
  display: "block",
};
